/**
 * What to do with the fan's pointer to their current order
 * (`idseat_pending_order_<tag>` in localStorage) after re-reading the order.
 *
 * The pointer is the only thing that lets a fan find their order again after
 * a reload, so it is removed as late as possible:
 *   - never on a transient error (callers keep it when the RPC fails);
 *   - a terminal order (delivered, collected, cancelled, refunded) stays
 *     visible for TERMINAL_GRACE_MS after it was first seen finished, or
 *     until the fan taps "Order again" — a refresh right after delivery used
 *     to show an empty menu as if nothing had been ordered;
 *   - a pending_payment order is abandoned only once it is older than
 *     PENDING_PAYMENT_ABANDON_MS (the webhook can lag a redirect return);
 *   - "not found" is the one definitive reason to remove it.
 */
export const PENDING_PAYMENT_ABANDON_MS = 30 * 60_000;
export const TERMINAL_GRACE_MS = 3 * 60 * 60_000;
/**
 * Longer than any match day. An order older than this is never shown again,
 * whatever its status: a delivery the fan's phone never heard about must not
 * greet them a week later, and an order the club never marked delivered must
 * not keep "being prepared" (and the 30 s poll) alive at the next match.
 */
export const MAX_ORDER_AGE_MS = 12 * 60 * 60_000;

export type PendingOrderAction = 'keep' | 'remove' | 'abandon';

export interface PendingOrderSnapshot {
  status: string;
  payment_status?: string | null;
  created_at?: string | null;
}

export function isTerminalOrder(order: PendingOrderSnapshot): boolean {
  return (
    order.payment_status === 'refunded' ||
    order.status === 'delivered' ||
    order.status === 'collected' ||
    order.status === 'cancelled'
  );
}

export function resolvePendingOrder(args: {
  order: PendingOrderSnapshot | null;
  now: number;
  /** When this order was first seen in a terminal state, or null. */
  finishedAt: number | null;
  /** A cancel arriving right after the client saw the payment succeed. */
  staleCancel?: boolean;
}): PendingOrderAction {
  const { order, now, finishedAt, staleCancel = false } = args;
  if (!order) return 'remove';
  if (staleCancel) return 'keep';
  const createdAt = order.created_at ? Date.parse(order.created_at) : NaN;
  const age = Number.isFinite(createdAt) ? now - createdAt : 0;
  if (order.status === 'pending_payment') {
    return age >= PENDING_PAYMENT_ABANDON_MS ? 'abandon' : 'keep';
  }
  if (age > MAX_ORDER_AGE_MS) return 'remove';
  if (isTerminalOrder(order)) {
    if (finishedAt == null) return 'keep';
    return now - finishedAt > TERMINAL_GRACE_MS ? 'remove' : 'keep';
  }
  return 'keep';
}

/**
 * Where a returning fan lands. The ordering view while their order is still
 * moving (or a cart was left behind); otherwise home — the status pill under
 * the header covers a finished order, and a fan re-tapping the tag at minute
 * 70 to vote should not be met by yesterday's "Order delivered" screen.
 */
export function shouldLandOnOrdering(args: {
  pointerOrderId: string | null;
  finishedAt: number | null;
  hasSavedCart: boolean;
}): boolean {
  if (args.hasSavedCart) return true;
  return !!args.pointerOrderId && args.finishedAt == null;
}

// ── finishedAt stamp, persisted next to the pointer like `:paidAt` ──────────

export function finishedAtStorageKey(pointerKey: string): string {
  return `${pointerKey}:finishedAt`;
}

export function writeFinishedAt(pointerKey: string, orderId: string, at: number = Date.now()): void {
  localStorage.setItem(finishedAtStorageKey(pointerKey), JSON.stringify({ id: orderId, at }));
}

/** The stamp for `orderId`, or null (none, another order, malformed). */
export function readFinishedAt(pointerKey: string, orderId: string): number | null {
  try {
    const raw = localStorage.getItem(finishedAtStorageKey(pointerKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown } | null;
    if (!parsed || parsed.id !== orderId || typeof parsed.at !== 'number') return null;
    return parsed.at;
  } catch {
    return null;
  }
}

export function clearFinishedAt(pointerKey: string): void {
  localStorage.removeItem(finishedAtStorageKey(pointerKey));
}
